import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import { getCities } from '../services/citiesService';
import { getCategories } from '../services/categoriesService';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  Spin,
  Select,
  Upload,
  message,
  Row,
  Col,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { uploadImage } from '../services/uploadService';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  allPlaces,
  createPlace,
  deletePlace,
  getPlaces,
  updatePlace,
  geocodeAddress,
} from '../services/placesService';
import { FaEyeSlash } from 'react-icons/fa';

function PlacesAdmin() {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const [places, setPlaces] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [tempLatLng, setTempLatLng] = useState(null);
  const [form] = Form.useForm();
  const { Title } = Typography;

  const submittedCount = places.filter(place => place.status === 'submitted').length;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [placesData, citiesData, categoriesData] = await Promise.all([
          allPlaces(),
          getCities(),
          getCategories(),
        ]);
        setPlaces(placesData);
        setCities(citiesData);
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const geocoding = useRef(false);
  const setGeocoding = value => {
    geocoding.current = value;
    form.setFieldsValue({ geocoding: value });
  };
  const isGeocoding = () => geocoding.current;
  const [loadMapError, setLoadMapError] = useState(null);
  const [map, setMap] = useState(null);

  const position = useMemo(() => {
    const lat = form.getFieldValue('latitude');
    const lng = form.getFieldValue('longitude');
    return {
      lat: parseFloat(lat) || 37.7749,
      lng: parseFloat(lng) || -122.4194,
    };
  }, [form.getFieldValue('latitude'), form.getFieldValue('longitude')]);
  const onLoad = useCallback(
    map => {
      setMap(map);
      map.addListener('center_changed', () => {
        const center = map.getCenter();
        form.setFieldsValue({
          latitude: center.lat(),
          longitude: center.lng(),
        });
      });
    },
    [form]
  );

  const onLoadError = useCallback(error => {
    console.error('Error loading Google Maps:', error);
    setLoadMapError('Failed to load Google Maps');
  }, []);

  const handleMapClick = useCallback(
    event => {
      if (!isGeocoding()) {
        form.setFieldsValue({
          latitude: event.latLng.lat(),
          longitude: event.latLng.lng(),
        });
      }
    },
    [form]
  );

  const fetchPlaces = async () => {
    try {
      const data = await allPlaces();
      setPlaces(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (place = null) => {
    if (place) {
      setEditingPlace(place);
      form.setFieldsValue({
        name: place.name,
        description: place.description || '',
        address: place.address || '',
        phone: place.phone || '',
        website_url: place.website_url || '',
        image_url: place.image_url || '',
        latitude: place.latitude || '',
        longitude: place.longitude || '',
        city_id: cities[0].id,
        category_id: place.category_id,
        categories: place.categories || [],
        status: place.status,
      });
    } else {
      setEditingPlace(null);
      form.resetFields();
    }
    setOpenDialog(true);
  };

  const handleCloseModal = () => {
    setOpenDialog(false);
    setEditingPlace(null);
    form.resetFields();
  };

  const handleSubmit = async values => {
    setLoading(true);
    values.city_id = cities[0].id;
    try {
      if (editingPlace) {
        await updatePlace(editingPlace.id, values);
      } else {
        await createPlace(values);
      }
      fetchPlaces();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: 'Are you sure you want to delete this place?',
      onOk: async () => {
        setLoading(true);
        try {
          await deletePlace(id);
          fetchPlaces();
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Category',
      dataIndex: ['category', 'name'],
      key: 'category',
      sorter: (a, b) => (a.category?.name || '').localeCompare(b.category?.name || ''),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      sorter: (a, b) => (a.address || '').localeCompare(b.address || ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: status => {
        switch (status) {
          case 'active':
            return <span style={{ color: 'black' }}>Active</span>;
          case 'closed':
            return <span style={{ color: 'black' }}>Closed</span>;
          case 'temporary_closed':
            return <span style={{ color: 'black' }}>Temporary Closed</span>;
          case 'submitted':
            return (
              <span style={{ color: 'orange' }}>
                {' '}
                <FaEyeSlash /> Submitted
              </span>
            );
          case 'inactive':
            return (
              <span style={{ color: 'grey' }}>
                <FaEyeSlash />
                Inactive
              </span>
            );
          case 'disabled':
            return (
              <span style={{ color: 'grey' }}>
                {' '}
                <FaEyeSlash /> Disabled
              </span>
            );
          default:
            return status;
        }
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  if (error) return <Alert message={error} type="error" />;

  if (loadError) {
    return (
      <Alert
        message="Failed to load Google Maps. Please check your API key and network connection."
        type="error"
      />
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
        <p>Loading Google Maps...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {loadError ? (
        <Alert
          message="Google Maps Error"
          description={loadError}
          type="error"
          style={{ marginBottom: '24px' }}
        />
      ) : null}
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2}>Places</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
          Add Place
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input.Search
            placeholder="Search places..."
            allowClear
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 200 }}
          />
          <Select
            placeholder="Filter by status"
            allowClear
            onChange={value => setStatusFilter(value)}
            style={{ width: 200 }}
          >
            <Select.Option value="active">Active</Select.Option>
            <Select.Option value="closed">Closed</Select.Option>
            <Select.Option value="temporary_closed">Temporary Closed</Select.Option>
            <Select.Option value="submitted">Submitted</Select.Option>
            <Select.Option value="inactive">Inactive</Select.Option>
            <Select.Option value="disabled">Disabled</Select.Option>
          </Select>
          {statusFilter > 0 && (
            <Button type={'primary'} onClick={() => setStatusFilter('submitted')}>
              Submitted ({submittedCount})
            </Button>
          )}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={places.filter(place => {
          const matchesSearch = searchText
            ? place.name.toLowerCase().includes(searchText.toLowerCase()) ||
              place.description?.toLowerCase().includes(searchText.toLowerCase()) ||
              place.address?.toLowerCase().includes(searchText.toLowerCase())
            : true;

          const matchesStatus = statusFilter ? place.status === statusFilter : true;

          return matchesSearch && matchesStatus;
        })}
        rowKey="id"
      />

      <Modal
        title={editingPlace ? 'Edit Place' : 'Add Place'}
        open={openDialog}
        onCancel={handleCloseModal}
        footer={null}
        width={1000}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please input the place name!' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select a status!' }]}
              >
                <Select>
                  <Select.Option value="active">Active</Select.Option>
                  <Select.Option value="closed">Closed</Select.Option>
                  <Select.Option value="temporary_closed">Temporary Closed</Select.Option>
                  <Select.Option value="inactive">
                    <FaEyeSlash color="grey" /> Inactive
                  </Select.Option>
                  <Select.Option value="disabled">
                    <FaEyeSlash color="grey" /> Disabled
                  </Select.Option>
                </Select>
              </Form.Item>
              <Form.Item hidden name="city_id" label="City">
                <Select>
                  {cities.map(city => (
                    <Select.Option key={city.id} value={city.id}>
                      {city.name}, {city.state}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="address" label="Address">
                <Input />
              </Form.Item>
              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>
              <Form.Item name="website_url" label="Website URL">
                <Input />
              </Form.Item>
              <Form.Item label="Location">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {/* <Button
                      onClick={async () => {
                        const address = form.getFieldValue("address");
                        if (!address) {
                          message.error("Please enter an address first");
                          return;
                        }
                        try {
                          setGeocoding(true);
                          const location = await geocodeAddress(address);
                          form.setFieldsValue({
                            latitude: location.lat,
                            longitude: location.lng,
                          });
                        } catch (error) {
                          message.error(error.message);
                        } finally {
                          setGeocoding(false);
                        }
                      }}
                      loading={geocoding}
                    >
                      Center on Address
                    </Button> */}
                  <div
                    style={{
                      height: '360px',
                      width: '100%',
                      marginBottom: '10px',
                    }}
                  >
                    <LocationPicker form={form} />
                  </div>
                </Space>

                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item name="latitude" label="Latitude" noStyle>
                      <Input type="number" step="any" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="longitude" label="Longitude" noStyle>
                      <Input type="number" step="any" />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="image_url" label="Image">
                <Upload
                  maxCount={1}
                  accept="image/*"
                  beforeUpload={async file => {
                    try {
                      const url = await uploadImage(file);
                      form.setFieldsValue({ image_url: url });
                      message.success('Image uploaded successfully');
                    } catch (err) {
                      message.error('Failed to upload image');
                    }
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload Image</Button>
                </Upload>
                {form.getFieldValue('image_url') && (
                  <img
                    src={form.getFieldValue('image_url')}
                    alt="Preview"
                    style={{
                      marginTop: 8,
                      maxWidth: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                    }}
                  />
                )}
              </Form.Item>
              <Form.Item name="category_id" label="Primary Category">
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {categories.map(category => (
                    <Select.Option key={category.id} value={category.id}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="categories" label="Additional Categories">
                <Select
                  mode="multiple"
                  placeholder="Select additional categories"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {categories.map(category => (
                    <Select.Option key={category.id} value={category.id}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingPlace ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// Location Picker Component
function LocationPicker({ form }) {
  const [mapError, setMapError] = useState(null);
  const [map, setMap] = useState(null);
  const [searchBox, setSearchBox] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [tempLatLng, setTempLatLng] = useState(null);
  const position = useMemo(() => {
    const lat = form.getFieldValue('latitude');
    const lng = form.getFieldValue('longitude');
    return {
      lat: parseFloat(lat) || 37.7749,
      lng: parseFloat(lng) || -122.4194,
    };
  }, [form.getFieldValue('latitude'), form.getFieldValue('longitude')]);

  const onLoad = useCallback(
    map => {
      setMap(map);
      map.addListener('center_changed', () => {
        const center = map.getCenter();

        // form.setFieldsValue({
        //   latitude: center.lat(),
        //   longitude: center.lng(),
        // });
        setTempLatLng({ lat: center.lat(), lng: center.lng() });
      });
    },
    [form]
  );

  const onLoadError = useCallback(error => {
    console.error('Error loading Google Maps:', error);
    setMapError('Failed to load Google Maps');
  }, []);

  const onSearchBoxLoad = useCallback(ref => {
    setSearchBox(ref);
  }, []);

  const onPlacesChanged = useCallback(() => {
    if (searchBox) {
      const places = searchBox.getPlaces();
      if (places.length === 0) return;

      const place = places[0];
      const location = place.geometry.location;
      map.panTo({ lat: location.lat(), lng: location.lng() });

      // form.setFieldsValue({
      //   latitude: location.lat(),
      //   longitude: location.lng(),
      // });
      setTempLatLng({ lat: location.lat(), lng: location.lng() });
    }
  }, [searchBox, form]);

  if (mapError) {
    return <Alert message={mapError} type="error" />;
  }

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        width: '100%',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1,
          width: '80%',
          maxWidth: '400px',
        }}
      >
        <StandaloneSearchBox onLoad={onSearchBoxLoad} onPlacesChanged={onPlacesChanged}>
          <input
            type="text"
            placeholder="Search for a location"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            style={{
              width: '100%',
              height: '40px',
              padding: '0 12px',
              borderRadius: '4px',
              border: '1px solid #d9d9d9',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
              fontSize: '14px',
            }}
          />
        </StandaloneSearchBox>
      </div>

      <GoogleMap
        mapContainerStyle={{ width: '100%', height: 'calc(100% - 40px)' }}
        center={position}
        key={`${position.lat}-${position.lng}`}
        zoom={13}
        mapTypeId="hybrid"
        onLoad={onLoad}
        options={{
          mapTypeId: 'hybrid',
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
        }}
      ></GoogleMap>
      <div
        style={{
          borderRadius: '4px',
          marginBottom: 30,
          padding: '8px 8px',
          display: 'flex',
          gap: 10,
          position: 'relative',
        }}
      >
        <div>
          {tempLatLng && (
            <div
              style={{
                border: '1px solid #ccc',
                padding: '4px 8px',
                borderRadius: 6,
                cursor: 'pointer',
              }}
              onClick={() => {
                form.setFieldsValue({
                  latitude: tempLatLng.lat,
                  longitude: tempLatLng.lng,
                });
              }}
            >
              Set Location
            </div>
          )}
        </div>
        {tempLatLng && (
          <div
            style={{
              padding: '4px 2px',
            }}
          >
            {tempLatLng?.lat}, {tempLatLng?.lng}
          </div>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            width: '20px',
            height: '20px',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: '4px',
              height: '20px',
              background: 'rgb(255, 0, 0)',

              left: '50%',
              transform: 'translateX(-50%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              width: '20px',
              height: '4px',
              background: 'rgb(255, 0, 0)',

              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default PlacesAdmin;
